import { useState } from "react";
import { Send, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, TELEGRAM_URL, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-[#0C0C12] border border-[#282836] rounded-xl px-4 py-3.5 text-[#F4F4F8] placeholder:text-[#63637A] focus:outline-none focus:border-[#FF5500] transition-colors";

export const ContactSection = ({ apps }) => {
  const [form, setForm] = useState({ name: "", contact: "", app_interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", contact: "", app_interest: "", message: "" });
      toast.success("Message sent — I'll get back to you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 border-t border-[#282836]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#FF5500] mb-4">Holler at me</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-16 max-w-2xl">
            Questions or ready to order? <span className="text-outline">Two easy ways to reach me.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal delay={0.1}>
            <a
              data-testid="contact-telegram-card"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="card-glow group block h-full bg-[#1A1A26] border border-[#282836] rounded-3xl p-10 relative overflow-hidden"
            >
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#FF5500]/10 blur-3xl group-hover:bg-[#FF5500]/20 transition-colors duration-700" />
              <Send className="w-10 h-10 text-[#FF5500] mb-8" />
              <h3 className="font-display text-2xl font-bold mb-3">Fastest — Telegram</h3>
              <p className="text-[#9E9EB3] leading-relaxed mb-10">
                The quickest way to ask questions or place an order. Message me directly and I'll walk you through
                options, pricing and setup — one on one.
              </p>
              <span className="inline-flex items-center gap-2 font-mono2 text-sm text-[#FF5500] uppercase tracking-[0.15em]">
                @cowboyapps <Send className="w-4 h-4" />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.2}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="card-glow bg-[#1A1A26] border border-[#282836] rounded-3xl p-10"
            >
              <div className="flex items-center gap-3 mb-8">
                <Mail className="w-6 h-6 text-[#FF5500]" />
                <h3 className="font-display text-2xl font-bold">Send a message</h3>
              </div>

              {sent && (
                <div data-testid="contact-success-message" className="mb-6 flex items-center gap-3 border border-[#FF5500]/40 bg-[#FF5500]/10 text-[#FF9900] rounded-xl px-4 py-3 text-sm">
                  <CheckCircle2 className="w-5 h-5 shrink-0" /> Message delivered to info@cowboyapps.net
                </div>
              )}

              <div className="space-y-4">
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
                <input
                  data-testid="contact-info-input"
                  required
                  value={form.contact}
                  onChange={set("contact")}
                  placeholder="Email, phone or Telegram handle"
                  className={inputCls}
                />
                <select
                  data-testid="contact-app-select"
                  value={form.app_interest}
                  onChange={set("app_interest")}
                  className={`${inputCls} appearance-none ${form.app_interest ? "" : "text-[#63637A]"}`}
                >
                  <option value="">Which app are you interested in? (optional)</option>
                  {apps.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                  <option value="A different app">A different app (tell me which one)</option>
                </select>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell me what you need..."
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="btn-brand w-full inline-flex items-center justify-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-7 py-4 rounded-xl disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Sending..." : "Send message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
